import { lazy, Suspense, useCallback } from 'react';
import {
  ControlSizesMD,
  InlineButton,
  InlineButtonAlignments,
} from '@picsart/design-system/components/InlineButton';
import { IconArrowLeft } from '@picsart/design-system/foundation/icons/IconArrowLeft';

import { useAppStore, type IAppStore } from '@/store';
import { Screen } from '@/types';

import useStyles from './styles';

const Loading = lazy(() => import('@/components/Loading'));

const selector = (store: IAppStore) => ({
  screen: store.screen,
  setScreen: store.setScreen,
  resetCreate: store.resetCreate,
});

/**
 * Full-screen "Generating…" overlay. Self-mounted: renders only while
 * `screen === 'loading'`, so the shell renders it unconditionally without
 * having to know the state condition.
 */
const ResultOverlay = () => {
  const classes = useStyles();
  const { screen, setScreen, resetCreate } = useAppStore(selector);

  const handleClose = useCallback(() => {
    resetCreate();
    setScreen(Screen.Files);
  }, [resetCreate, setScreen]);

  if (screen !== Screen.Loading) return null;

  return (
    <div className={classes.fullScreenOverlay}>
      <div className={classes.overlayHeader}>
        <InlineButton
          size={ControlSizesMD}
          startIcon={IconArrowLeft}
          alignment={InlineButtonAlignments.Start}
          isWithoutBorder
          isWithoutBackground
          isFullWidth
          onClick={handleClose}
          aria-label="Back to creations"
          dataTestId="result-overlay-back"
        >
          Generating...
        </InlineButton>
      </div>
      <div className={classes.overlayContent}>
        <Suspense fallback={null}>
          <Loading />
        </Suspense>
      </div>
    </div>
  );
};

export default ResultOverlay;
